import clsx from "clsx";
import { forwardRef, HTMLAttributes } from "react";
import { CheckSquare, MinusSquare, Square } from "lucide-react";
import { twMerge } from "tailwind-merge";
import Badge from "../badge";
import OverlayTrigger from "../overlaytrigger";
import Routing from "../routing";
import Tooltip from "../tooltip";

export type NavigationItemStatus =
  | "None"
  | "Incomplete"
  | "Partial"
  | "Complete";

export interface NavigationItemProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "onClick"
> {
  tooltip?: string;
  route?: string;
  children: React.ReactNode;
  active?: boolean;
  isActive?: (route?: string) => boolean;
  invalid?: boolean;
  disabled?: boolean;
  status?: NavigationItemStatus;
  onClick?: () => void;
  badge?: string;
  endAdornment?: React.ReactNode;
}

export const NavigationItem = forwardRef<HTMLDivElement, NavigationItemProps>(
  (props, ref) => {
    const {
      tooltip,
      disabled,
      route,
      status,
      invalid,
      badge,
      children,
      active: activeParam,
      isActive,
      onClick,
      className,
      endAdornment,
      ...rest
    } = props;

    const active = activeParam ?? isActive?.(route);

    const component = (
      <div
        {...rest}
        ref={ref}
        className={twMerge(
          clsx(
            "select-none px-2 py-1.5 flex items-center rounded-sm transition-colors duration-300",
            {
              "bg-primary/75": active && !invalid,
              "bg-red-500/75": active && invalid,
              "text-white": active,
              "text-secondary": !active,
              "hover:bg-slate-100 cursor-pointer": !active,
              "cursor-not-allowed": disabled,
            },
          ),
          className,
        )}
        onClick={disabled ? undefined : onClick}
        role="none"
      >
        {status === "Incomplete" && <Square size="16" className="me-2" />}
        {status === "Complete" && <CheckSquare size="16" className="me-2" />}
        {status === "Partial" && <MinusSquare size="16" className="me-2" />}
        <span className="flex-1 whitespace-nowrap">{children}</span>
        {!!badge && (
          <Badge
            className={clsx({
              "text-primary-dark": active && !invalid,
              "text-red-500": active && invalid,
              "bg-white/75": active,
              "bg-primary/75": !active,
              "mx-1": true,
            })}
          >
            {badge}
          </Badge>
        )}
        {endAdornment}
      </div>
    );

    if (tooltip) {
      return (
        <OverlayTrigger overlay={<Tooltip>{tooltip}</Tooltip>}>
          {component}
        </OverlayTrigger>
      );
    }

    return component;
  },
);

export const NavigationLink = forwardRef<HTMLDivElement, NavigationItemProps>(
  (props, ref) => {
    const { disabled, route, active } = props;

    if (disabled || !route) {
      return <NavigationItem {...props} ref={ref} />;
    }

    return (
      <Routing tabIndex={-1} to={route}>
        {({ isActive }) => (
          <NavigationItem {...props} active={active ?? isActive} ref={ref} />
        )}
      </Routing>
    );
  },
);

export default NavigationLink;
